// Material tracking lot (MTN) state shown by the Material page: the canonical remaining,
// the lot badge and whether a lot can source a transfer. Pure, so they are unit-tested
// (mtnLot.test.js); jobq-api's /material_tracking is the source of truth for all three.

// A lot's remaining: jobq-api's canonical remaining_qty, or the same formula for a
// stale API response mid-deploy. Everything a lot can lose comes off it — scanned
// usage, the manual usage_adjustment, transferred_out_quantity AND returned_quantity
// (sent back to the owner by a customer material return). Deriving from used_qty
// alone is what made fully-transferred lots read "Unused" (steel on the floor)
// when their whole quantity had moved to a child lot; leaving out returns made
// every lot that fed a return read as still on hand.
export function mtnRemaining(row) {
    if (row.remaining_qty != null) return row.remaining_qty;
    return (row.quantity || 0) - (row.used_qty || 0)
        - (row.transferred_out_quantity || 0) - (row.returned_quantity || 0);
}

// MTN lot state for the badge. `lot_status` is computed by jobq-api's
// /material_tracking (single source of truth, shared with the status dropdown);
// the local fallback only covers a stale API response mid-deploy. 'over' = more
// left the lot (cut, transferred out, returned) than it received: remaining < 0,
// the same test jobq-api's lot_status and 'flagged' filter use.
export function mtnLotBadge(row) {
    const status = row.lot_status || (() => {
        const qty = row.quantity || 0;
        const remaining = mtnRemaining(row);
        if (row.reconciled_at) return 'reconciled';
        if (remaining < 0) return 'over';
        if (remaining <= 0 && (row.returned_quantity || 0) > 0) return 'returned';
        if (remaining <= 0 && (row.transferred_out_quantity || 0) > 0) return 'transferred';
        if (remaining <= 0) return 'exhausted';
        if (remaining >= qty) return 'unused';
        return 'active';
    })();

    switch (status) {
        case 'reconciled':
            return { label: 'Reconciled', color: 'secondary' };
        case 'over':
            return {
                label: `Over +${-mtnRemaining(row)}`,
                color: 'danger',
                title: `More left this lot than it received: ${row.used_qty || 0} used`
                    + ((row.transferred_out_quantity || 0) ? `, ${row.transferred_out_quantity} transferred out` : '')
                    + ((row.returned_quantity || 0) ? `, ${row.returned_quantity} returned` : '')
                    + ` of ${row.quantity || 0} received`,
            };
        case 'transferred': {
            const to = row.transferred_to_mtn_ids || [];
            const first = to.length ? String(to[0]).padStart(6, '0') : null;
            return {
                label: first
                    ? `Transferred → ${first}${to.length > 1 ? ` +${to.length - 1}` : ''}`
                    : 'Transferred',
                color: 'dark',
                title: to.length
                    ? `All ${row.quantity} moved to tracking number${to.length > 1 ? 's' : ''} `
                      + to.map(n => String(n).padStart(6, '0')).join(', ')
                    : 'Quantity moved to another tracking number',
            };
        }
        case 'returned': {
            // A lot that also fed a transfer keeps the link to its child lot here.
            const to = row.transferred_to_mtn_ids || [];
            return {
                label: 'Returned',
                color: 'dark',
                title: `${row.returned_quantity || 0} of ${row.quantity} returned to the owner`
                    + (row.owner_name ? ` (${row.owner_name})` : '')
                    + ((row.transferred_out_quantity || 0)
                        ? `; ${row.transferred_out_quantity} moved to `
                          + (to.length ? to.map(n => String(n).padStart(6, '0')).join(', ') : 'another tracking number')
                        : ''),
            };
        }
        case 'exhausted':
            return { label: 'Exhausted', color: 'dark' };
        case 'unused':
            return { label: 'Unused', color: 'info' };
        default: {
            // 'Active' also covers a lot that lost units to a return or a transfer
            // rather than to the laser; say so on hover.
            const moved = [];
            if (row.returned_quantity) moved.push(`${row.returned_quantity} returned to the owner`);
            if (row.transferred_out_quantity) moved.push(`${row.transferred_out_quantity} transferred out`);
            return {
                label: 'Active',
                color: 'success',
                title: moved.length
                    ? `${mtnRemaining(row)} of ${row.quantity} left; ${moved.join(', ')}`
                    : undefined,
            };
        }
    }
}

// Can this lot be the SOURCE of a material transfer? Only steel that is
// physically on the floor and still open: 'active' (partially cut) or 'unused'
// (untouched). Everything else is off-limits:
//   exhausted / transferred / returned — nothing left to move
//   over                    — a discrepancy, not a stack you draw from
//   reconciled              — RETIRED. Its leftover `remaining_qty` is exactly
//                             the variance the physical count wrote off, so
//                             offering it would transfer steel that isn't there.
// Deriving this from `remaining_qty > 0` alone is what put four reconciled lots
// (TRK 235/234/86/20, 9 phantom sheets) in the picker on 11GA-CSB-HR-48x120.
// `lot_status` is computed by jobq-api's /material_tracking (single source of
// truth, shared with the badge and the status dropdown); the local fallback only
// covers a stale API response mid-deploy.
export function isTransferableMtn(row) {
    if (!row) return false;
    if (row.lot_status) return row.lot_status === 'active' || row.lot_status === 'unused';
    if (row.reconciled_at) return false;
    return mtnRemaining(row) > 0;   // an 'over' lot (remaining < 0) is never a source
}
